import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["searchInput", "categoryFilter", "servicesGrid", "saveButton", "statusMessage", "serviceCount"]
  static values = { staffId: Number, assignedServices: Array, customPrices: Object, customDurations: Object }
  static classes = ["assigned", "unassigned"]

  connect() {
    this.originalAssignedServices = [...this.assignedServicesValue]
    this.currentAssignedServices = [...this.assignedServicesValue]
    this.currentCustomPrices = { ...this.customPricesValue }
    this.currentCustomDurations = { ...this.customDurationsValue }
    this.originalCustomPrices = { ...this.customPricesValue }
    this.originalCustomDurations = { ...this.customDurationsValue }
    this.updateSaveButtonState()
  }

  // Prevent clicks on pricing fields from toggling the service
  stopPropagation(event) {
    event.stopPropagation()
  }

  // Toggle individual service assignment
  toggleService(event) {
    const serviceElement = event.currentTarget
    const serviceId = parseInt(serviceElement.dataset.serviceId)
    const isCurrentlyAssigned = this.currentAssignedServices.includes(serviceId)

    if (isCurrentlyAssigned) {
      // Remove service and clear custom values
      this.currentAssignedServices = this.currentAssignedServices.filter(id => id !== serviceId)
      delete this.currentCustomPrices[serviceId.toString()]
      delete this.currentCustomDurations[serviceId.toString()]
      this.updateServiceVisual(serviceElement, false)
      this.clearPricingFields(serviceElement)
    } else {
      // Add service
      this.currentAssignedServices.push(serviceId)
      this.updateServiceVisual(serviceElement, true)
    }

    this.togglePricingFields(serviceElement, !isCurrentlyAssigned)
    this.updateSaveButtonState()
    this.updateServiceCount()
  }

  // Update custom price value
  updateCustomPrice(event) {
    const serviceId = event.currentTarget.dataset.serviceId
    const value = event.currentTarget.value
    if (value && parseFloat(value) > 0) {
      this.currentCustomPrices[serviceId] = value
    } else {
      delete this.currentCustomPrices[serviceId]
    }
    this.updateSaveButtonState()
  }

  // Update custom duration value
  updateCustomDuration(event) {
    const serviceId = event.currentTarget.dataset.serviceId
    const value = event.currentTarget.value
    if (value) {
      this.currentCustomDurations[serviceId] = value
    } else {
      delete this.currentCustomDurations[serviceId]
    }
    this.updateSaveButtonState()
  }

  // Show/hide pricing fields for a service card
  togglePricingFields(element, show) {
    const pricingFields = element.querySelector('.pricing-fields')
    if (pricingFields) {
      pricingFields.classList.toggle('hidden', !show)
    }
  }

  // Clear pricing field inputs
  clearPricingFields(element) {
    const priceInput = element.querySelector('input[type="number"]')
    const durationSelect = element.querySelector('select')
    if (priceInput) priceInput.value = ''
    if (durationSelect) durationSelect.value = ''
  }

  // Restore pricing field values from state
  restorePricingFields(element, serviceId) {
    const priceInput = element.querySelector('input[type="number"]')
    const durationSelect = element.querySelector('select')
    const sid = serviceId.toString()
    if (priceInput) priceInput.value = this.currentCustomPrices[sid] || ''
    if (durationSelect) durationSelect.value = this.currentCustomDurations[sid] || ''
  }

  // Update visual state of service card
  updateServiceVisual(element, assigned) {
    const toggle = element.querySelector('.service-toggle svg')

    if (assigned) {
      // Assigned state - use static classes
      this.unassignedClasses.forEach(cls => element.classList.remove(cls))
      this.assignedClasses.forEach(cls => element.classList.add(cls))
      toggle.outerHTML = `
        <svg class="w-5 h-5 text-black" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
        </svg>
      `
    } else {
      // Unassigned state - use static classes
      this.assignedClasses.forEach(cls => element.classList.remove(cls))
      this.unassignedClasses.forEach(cls => element.classList.add(cls))
      toggle.outerHTML = `
        <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
        </svg>
      `
    }
  }

  // Select all visible services
  selectAll(event) {
    event.preventDefault()
    const visibleServices = this.getVisibleServices()

    visibleServices.forEach(element => {
      const serviceId = parseInt(element.dataset.serviceId)
      if (!this.currentAssignedServices.includes(serviceId)) {
        this.currentAssignedServices.push(serviceId)
        this.updateServiceVisual(element, true)
        this.togglePricingFields(element, true)
      }
    })

    this.updateSaveButtonState()
    this.updateServiceCount()
  }

  // Clear all assigned services
  clearAll(event) {
    event.preventDefault()
    const visibleServices = this.getVisibleServices()

    visibleServices.forEach(element => {
      const serviceId = parseInt(element.dataset.serviceId)
      if (this.currentAssignedServices.includes(serviceId)) {
        this.currentAssignedServices = this.currentAssignedServices.filter(id => id !== serviceId)
        delete this.currentCustomPrices[serviceId.toString()]
        delete this.currentCustomDurations[serviceId.toString()]
        this.updateServiceVisual(element, false)
        this.togglePricingFields(element, false)
        this.clearPricingFields(element)
      }
    })

    this.updateSaveButtonState()
    this.updateServiceCount()
  }

  // Get currently visible service elements
  getVisibleServices() {
    return Array.from(this.servicesGridTarget.querySelectorAll('.service-item:not([style*="display: none"])'))
  }

  // Filter services based on search and category
  filterServices() {
    const searchTerm = this.searchInputTarget.value.toLowerCase()
    const selectedCategory = this.categoryFilterTarget.value

    const serviceElements = this.servicesGridTarget.querySelectorAll('.service-item')

    serviceElements.forEach(element => {
      const serviceName = element.dataset.serviceName.toLowerCase()
      const serviceCategory = element.dataset.serviceCategory || ''

      const matchesSearch = serviceName.includes(searchTerm)
      const matchesCategory = !selectedCategory || serviceCategory === selectedCategory

      if (matchesSearch && matchesCategory) {
        element.style.display = 'block'
      } else {
        element.style.display = 'none'
      }
    })
  }

  // Cancel changes and reset to original state
  cancelChanges(event) {
    event.preventDefault()
    this.currentAssignedServices = [...this.originalAssignedServices]
    this.currentCustomPrices = { ...this.originalCustomPrices }
    this.currentCustomDurations = { ...this.originalCustomDurations }

    // Reset all service visuals and pricing fields
    const serviceElements = this.servicesGridTarget.querySelectorAll('.service-item')
    serviceElements.forEach(element => {
      const serviceId = parseInt(element.dataset.serviceId)
      const shouldBeAssigned = this.originalAssignedServices.includes(serviceId)
      this.updateServiceVisual(element, shouldBeAssigned)
      this.togglePricingFields(element, shouldBeAssigned)
      if (shouldBeAssigned) {
        this.restorePricingFields(element, serviceId)
      } else {
        this.clearPricingFields(element)
      }
    })

    this.updateSaveButtonState()
    this.updateServiceCount()
    this.showStatusMessage("Changes cancelled", "text-gray-600")
  }

  // Save changes to the server
  async saveChanges(event) {
    event.preventDefault()
    this.saveButtonTarget.disabled = true
    this.saveButtonTarget.textContent = "Saving..."

    try {
      const response = await fetch(`/operations/staffs/${this.staffIdValue}/assign_services`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          service_ids: this.currentAssignedServices,
          custom_prices: this.currentCustomPrices,
          custom_durations: this.currentCustomDurations
        })
      })

      if (response.ok) {
        this.originalAssignedServices = [...this.currentAssignedServices]
        this.assignedServicesValue = [...this.currentAssignedServices]
        this.originalCustomPrices = { ...this.currentCustomPrices }
        this.originalCustomDurations = { ...this.currentCustomDurations }
        this.customPricesValue = { ...this.currentCustomPrices }
        this.customDurationsValue = { ...this.currentCustomDurations }
        this.showStatusMessage("Services updated successfully!", "text-green-600")
        this.updateSaveButtonState()
      } else {
        throw new Error('Failed to save changes')
      }
    } catch (error) {
      console.error('Error saving services:', error)
      this.showStatusMessage("Error saving changes. Please try again.", "text-red-600")
    } finally {
      this.saveButtonTarget.disabled = false
      this.saveButtonTarget.textContent = "Save Changes"
    }
  }

  // Update save button enabled/disabled state
  updateSaveButtonState() {
    const servicesChanged = !this.arraysEqual(this.originalAssignedServices, this.currentAssignedServices)
    const pricesChanged = !this.objectsEqual(this.originalCustomPrices, this.currentCustomPrices)
    const durationsChanged = !this.objectsEqual(this.originalCustomDurations, this.currentCustomDurations)
    const hasChanges = servicesChanged || pricesChanged || durationsChanged

    this.saveButtonTarget.disabled = !hasChanges

    if (hasChanges) {
      this.saveButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
    } else {
      this.saveButtonTarget.classList.add('opacity-50', 'cursor-not-allowed')
    }
  }

  // Update service count display
  updateServiceCount() {
    this.serviceCountTarget.textContent = this.currentAssignedServices.length
  }

  // Show status message
  showStatusMessage(message, className) {
    this.statusMessageTarget.textContent = message
    this.statusMessageTarget.className = `mt-3 text-sm text-center ${className}`
    this.statusMessageTarget.classList.remove('hidden')

    setTimeout(() => {
      this.statusMessageTarget.classList.add('hidden')
    }, 3000)
  }

  // Helper function to compare arrays
  arraysEqual(a, b) {
    if (a.length !== b.length) return false
    const sortedA = [...a].sort((x, y) => x - y)
    const sortedB = [...b].sort((x, y) => x - y)
    return sortedA.every((val, i) => val === sortedB[i])
  }

  // Helper function to compare objects
  objectsEqual(a, b) {
    const keysA = Object.keys(a).sort()
    const keysB = Object.keys(b).sort()
    if (keysA.length !== keysB.length) return false
    return keysA.every((key, i) => key === keysB[i] && a[key] === b[key])
  }
}
